/**
 * ResourceCompositionSection Component
 *
 * Section 4 of the Gestão de Recursos Edit Page:
 * "Composição de Recursos, Investimentos e Patrocínios"
 *
 * Displays 7 auto-calculated read-only fields:
 * 1. Total de Itens Financiáveis (from Budget)
 * 2. Orçamento Total do Projeto (from Budget)
 * 3. Total de Investimentos e Patrocínios Captados (sum of contracts)
 * 4. Valor Captado para Cobertura de Itens Financiáveis (calculated)
 * 5. Saldo a Captar do Orçamento Total (calculated)
 * 6. Saldo a Captar de Itens Financiáveis (calculated)
 * 7. Total Final do Projeto (green highlight, calculated)
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - All fields read-only with disabled CurrencyInput
 * - Final total in success green #13D480
 *
 * Calculation Logic:
 * - valorCaptadoCobertura = Min(totalInvestimentos, totalFinanciaveis)
 * - saldoCaptarTotal = orcamentoTotal - totalInvestimentos
 * - saldoCaptarFinanciaveis = totalFinanciaveis - valorCaptadoCobertura
 * - totalFinalProjeto = orcamentoTotal (displayed in green)
 */

import { useMemo } from "react";

interface ResourceCompositionSectionProps {
  // Budget totals from Section 1
  totalFinanciaveis: number;
  orcamentoTotal: number;
  // Investment contracts total from Section 3
  totalInvestimentos: number;
}

export function ResourceCompositionSection({
  totalFinanciaveis,
  orcamentoTotal,
  totalInvestimentos,
}: ResourceCompositionSectionProps) {
  // Calculate all composition fields
  const composition = useMemo(() => {
    // Field 4: Minimum between captured investments and financeable items
    const valorCaptadoCobertura = Math.min(
      totalInvestimentos,
      totalFinanciaveis
    );

    // Field 5: Balance to capture from total budget
    const saldoCaptarTotal = orcamentoTotal - totalInvestimentos;

    // Field 6: Balance to capture for financeable items
    const saldoCaptarFinanciaveis = totalFinanciaveis - valorCaptadoCobertura;

    // Field 7: Final project total (same as budget total, shown in green)
    const totalFinalProjeto = orcamentoTotal;

    return {
      totalItensFinanciaveis: totalFinanciaveis, // Field 1
      orcamentoTotalProjeto: orcamentoTotal, // Field 2
      totalInvestimentosCaptados: totalInvestimentos, // Field 3
      valorCaptadoCobertura, // Field 4
      saldoCaptarTotal, // Field 5
      saldoCaptarFinanciaveis, // Field 6
      totalFinalProjeto, // Field 7
    };
  }, [totalFinanciaveis, orcamentoTotal, totalInvestimentos]);

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[40px]">
      {/* Section Title */}
      <h3 className="text-[20px] font-bold text-[#F6F6F6] mb-[30px]">
        Composição de Recursos, Investimentos e Patrocínios
      </h3>

      {/* Calculated Fields Grid - 2 columns */}
      <div className="grid grid-cols-2 gap-x-[30px] gap-y-[24px]">
        {/* Field 1: Total de Itens Financiáveis */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Total de Itens Financiáveis
          </label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 text-white border border-gray-600 opacity-70">
            <span className="text-sm font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(composition.totalItensFinanciaveis)}
            </span>
          </div>
        </div>

        {/* Field 2: Orçamento Total do Projeto */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Orçamento Total do Projeto
          </label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 text-white border border-gray-600 opacity-70">
            <span className="text-sm font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(composition.orcamentoTotalProjeto)}
            </span>
          </div>
        </div>

        {/* Field 3: Total de Investimentos e Patrocínios Captados */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Total de Investimentos e Patrocínios Captados
          </label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 text-white border border-gray-600 opacity-70">
            <span className="text-sm font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(composition.totalInvestimentosCaptados)}
            </span>
          </div>
        </div>

        {/* Field 4: Valor Captado para Cobertura de Itens Financiáveis */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Valor Captado para Cobertura de Itens Financiáveis
          </label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 text-white border border-gray-600 opacity-70">
            <span className="text-sm font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(composition.valorCaptadoCobertura)}
            </span>
          </div>
        </div>

        {/* Field 5: Saldo a Captar do Orçamento Total */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Saldo a Captar do Orçamento Total
          </label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 text-white border border-gray-600 opacity-70">
            <span className="text-sm font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(composition.saldoCaptarTotal)}
            </span>
          </div>
        </div>

        {/* Field 6: Saldo a Captar de Itens Financiáveis */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Saldo a Captar de Itens Financiáveis
          </label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 text-white border border-gray-600 opacity-70">
            <span className="text-sm font-bold">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(composition.saldoCaptarFinanciaveis)}
            </span>
          </div>
        </div>

        {/* Field 7: Total Final do Projeto (GREEN, full width) */}
        <div className="col-span-2 flex flex-col gap-2">
          <label className="text-[14px] text-[#13D480] font-semibold">
            Total Final do Projeto
          </label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-900 border border-[#13D480]">
            <span className="text-sm font-bold text-[#13D480]">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(composition.totalFinalProjeto)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
